/**
 * Base arsenal of the GDD (section 8) built at step 1: bazooka, grenade, pistol, sandbag. The flamethrower
 * and the Wave come with fire and water reactions. Values are the GDD's; the rest is tuning.
 */
import { WEAPON_PISTOL, WEAPON_DRILL, WEAPON_WAVE, WEAPON_FLAMER, WEAPON_METEOR, WEAPON_LASER, WEAPON_GEYSER, WEAPON_DOLL, WEAPON_REMOTE, WEAPON_JETPACK, WEAPON_VOLCANO } from './weapon-ids';
import { cosDeg, sinDeg, TRIG_ONE } from '@invaders/sim';
import { FP, FP_SHIFT, PFP, PFP_SHIFT, type GameState, type Unit } from './state';
import { UNIT_H, UNIT_W, putJetpackOn, startDrill, unitCenter } from './unit';
import { launchSurge } from './surge';
import { startJet } from './fire';
import { dropMeteor } from './projectile';
import { fireLaser, moveSquare, startGeyser, type RemoteMove } from './arsenal';
import { startVolcano } from './volcano';



export * from './weapon-ids';

/**
 * Order of the base arsenal in the weapon bar, as in the GDD table (section 8), then the author's additions: the mines
 * and the drill (chest weapons in the GDD), the battery, the jetpack.
 */
export const BAR_ORDER: readonly number[] = [0, 1, 7, 6, 3, 2, 5, 10, 17, 4, 16];

/**
 * 'charge': hold to charge the power, release to throw. 'direct': a click fires at once. 'hold': a jet while
 * held. 'target': a click designates a point of the map (meteor, geyser, pocket volcano). 'link': designate an enemy, then
 * throw (voodoo doll). 'remote': pick a square, move it, turn it with the wheel, drop it. 'jetpack': a click puts it on
 * (command 'jetpack', not a shot).
 */
export type FireMode = 'charge' | 'direct' | 'hold' | 'target' | 'link' | 'remote' | 'jetpack' | 'drill';

export interface Weapon {
  readonly name: string;
  readonly mode: FireMode;
  /** Ammunition a team starts with; -1 = unlimited. */
  readonly ammo: number;
  /** Explosion radius (bazooka, grenade), sand radius (sandbag), crumble radius (pistol). */
  readonly radius: number;
  /** Maximum damage. */
  readonly damage: number;
  /** Wind sensitivity in 1/4: 4 = full, 1 = "peu sensible au vent", 0 = none. */
  readonly wind: number;
  /** Only found in chests (the team starts with none). */
  readonly chest: boolean;
  /** Short label for the weapon bar. */
  readonly short: string;
  /**
   * Thrown by hand (author's rule): it leaves slower than the bazooka's shell and falls harder (MAX_THROW_SPEED,
   * THROW_GRAVITY), a clear arc; the client draws the start of that arc while the throw is charged.
   */
  readonly lob?: boolean;
  /** Percent of the blow of its explosion on what it throws away (BLAST_KNOCKBACK); 100 if absent. */
  readonly knock?: number;
}

/**
 * The weapons. Every damage is twice the GDD's (author's choice), except the bazooka's and the grenade's, set by hand since:
 * bazooka 48 (doubled, then halved), grenade 83 (208, less 60 %); pistol 15 → 30,
 * sandbag 12 → 24, mines 40 → 80, Wave 10 → 20, cryo-grenade 14 → 28, meteor 40 → 80, laser 30 → 60.
 */
export const WEAPONS: readonly Weapon[] = [
  // Their explosions throw units less far (author's choice): the grenade half as far, the bazooka 48 % (25 %, +50 %, then +25 %).
  // 3 each at the start, like the pistol, 2 for the drill (author's choice; unlimited in the GDD).
  { name: 'Bazooka', mode: 'charge', ammo: 3, radius: 30, damage: 48, wind: 4, chest: false, short: 'Bazooka', knock: 48 },
  { name: 'Grenade', mode: 'charge', ammo: 3, radius: 36, damage: 83, wind: 1, chest: false, short: 'Grenade', lob: true, knock: 50 },
  { name: 'Pistol', mode: 'direct', ammo: 3, radius: 2, damage: 30, wind: 0, chest: false, short: 'Pistol' },
  // Radius 28 instead of the GDD's 7: sixteen times the sand (author's choice: 14, then four times the sand again).
  { name: 'Sandbag', mode: 'charge', ammo: 3, radius: 28, damage: 24, wind: 4, chest: false, short: 'Sand', lob: true },
  // The unit bores its way, steered while the button is held, 3 s (author's change; the GDD digs a tunnel of 64 at
  // once); a tunnel 20 across (radius 10), rock included, stopped by obsidian. Not the turn's shot.
  // Base arsenal, 2 per team (author's choice; a chest weapon in the GDD); it digs obsidian too.
  { name: 'Drill', mode: 'drill', ammo: 2, radius: 10, damage: 0, wind: 0, chest: false, short: 'Drill' },
  // Thrown like a grenade (power 0: put at the feet); radius 9, 40 damage (80: doubled).
  // Base arsenal, 2 per team (author's choice; a chest weapon in the GDD).
  { name: 'Explosive mine', mode: 'charge', ammo: 2, radius: 9, damage: 80, wind: 1, chest: false, short: 'Mine', lob: true },
  // A wave 32 cells high (radius = its height), 10 damage on impact (20: doubled).
  { name: 'The Wave', mode: 'direct', ammo: 2, radius: 32, damage: 20, wind: 0, chest: false, short: 'Wave' },
  // Range 105 (radius), cone 44 cells wide at the end, 1.5 s.
  { name: 'Flamethrower', mode: 'hold', ammo: 2, radius: 105, damage: 0, wind: 0, chest: false, short: 'Flames' },
  // Spills burning oil (radius = cells of oil / 10).
  { name: 'Oil cocktail', mode: 'charge', ammo: 0, radius: 6, damage: 0, wind: 4, chest: true, short: 'Cocktail', lob: true },
  // Freezes a radius of 36, 14 damage (28: doubled); thrown like a grenade (3 s).
  { name: 'Cryo grenade', mode: 'charge', ammo: 0, radius: 36, damage: 28, wind: 1, chest: true, short: 'Cryo', lob: true },
  // Goes off like the explosive mine; freezes a radius of 30.
  // Base arsenal, 2 per team (author's choice; a chest weapon in the GDD).
  { name: 'Ice mine', mode: 'charge', ammo: 2, radius: 30, damage: 0, wind: 1, chest: false, short: 'Ice mine', lob: true },
  // A block of ice 14 × 14 (radius 7) falls straight down on the point: 40 crush damage (80: doubled).
  { name: 'Ice meteor', mode: 'target', ammo: 0, radius: 7, damage: 80, wind: 0, chest: true, short: 'Meteor' },
  // A ray (arsenal.ts: it flies, bounces on ice and walls), 30 damage to each unit it crosses (60: doubled).
  { name: 'Laser cannon', mode: 'direct', ammo: 0, radius: 120, damage: 60, wind: 0, chest: true, short: 'Laser' },
  // A column of water and steam (radius: half its width) for 2 s, 5 s into the next player's turn (arsenal.ts).
  // 27 cells wide: three times the GDD's 9 (author's choice).
  { name: 'Geyser', mode: 'target', ammo: 0, radius: 13, damage: 0, wind: 0, chest: true, short: 'Geyser' },
  // Thrown like a grenade (harmless); the linked enemy copies its flight.
  { name: 'Voodoo doll', mode: 'link', ammo: 0, radius: 0, damage: 0, wind: 4, chest: true, short: 'Doll', lob: true },
  // A square of 96 (radius 48; 32 in the GDD, tripled by the author), moved anywhere, turned by steps of 15°.
  { name: 'Remote', mode: 'remote', ammo: 0, radius: 48, damage: 0, wind: 0, chest: true, short: 'Remote' },
  // Author's addition: 2 per team, 6 s of thrust each (JETPACK_FUEL); flying is not the turn's shot.
  { name: 'Jetpack', mode: 'jetpack', ammo: 2, radius: 0, damage: 0, wind: 0, chest: false, short: 'Jetpack' },
  // Author's addition: thrown like a grenade, it stays where it comes to rest and discharges (radius 12 and the water
  // connected to it, 20 damage) at the start of each of the next BATTERY_TURNS turns. 1 per team.
  { name: 'Battery', mode: 'charge', ammo: 1, radius: 12, damage: 20, wind: 1, chest: false, short: 'Battery', lob: true },
  // Author's addition, chest weapon: thrown like a grenade, it rolls from where it lands and leaves a big trail (barrel.ts).
  { name: 'Acid barrel', mode: 'charge', ammo: 0, radius: 6, damage: 20, wind: 1, chest: true, short: 'Acid', lob: true },
  { name: 'Powder barrel', mode: 'charge', ammo: 0, radius: 26, damage: 120, wind: 1, chest: true, short: 'Powder', lob: true },
  // Author's addition, chest weapon: put like the geyser; it erupts for 3 turns, then melts into lava (volcano.ts).
  { name: 'Pocket volcano', mode: 'target', ammo: 0, radius: 20, damage: 10, wind: 0, chest: true, short: 'Volcano' },
  // Author's addition, chest weapon: thrown like a grenade, it walks about and swallows what is not solid (roomba.ts).
  { name: 'Roomba', mode: 'charge', ammo: 0, radius: 26, damage: 0, wind: 1, chest: true, short: 'Roomba', lob: true },
];

export function isWeapon(weapon: number): boolean {
  return Number.isInteger(weapon) && weapon >= 0 && weapon < WEAPONS.length;
}

/** Launch speed at full power, in cells per tick (fixed-point PFP). */
export const MAX_LAUNCH_SPEED = 8 << PFP_SHIFT;
/**
 * A weapon thrown by hand (Weapon.lob) leaves at 6 cells per tick at full power and falls twice as hard
 * (THROW_GRAVITY): at 45°, about 384 cells away in 1.5 s, 96 cells high at the top (author's rule; values chosen).
 */
export const MAX_THROW_SPEED = 6 << PFP_SHIFT;
/** The pistol's bullet flies this many cells per tick (author's rule: it has no range); its blow on a unit. */
export const BULLET_SPEED = 14;
export const PISTOL_RECOIL = FP;

/**
 * Fires `weapon` from the unit along `angle` (integer degrees, 0 = right, 90 = up) with `power` (0-100).
 * Ammunition is checked by the rules, not here.
 */
/** Extra data of a shot: the doll's linked unit, the remote's move. */
export interface FireExtra {
  link?: number;
  remote?: RemoteMove;
}

export function fireWeapon(state: GameState, unit: Unit, weapon: number, angle: number, power: number, target?: [number, number], extra?: FireExtra): void {
  unit.facing = cosDeg(angle) < 0 ? -1 : 1;
  state.events.push({ type: 'launch', weapon, unit: unit.id });
  if (weapon === WEAPON_PISTOL) {
    firePistol(state, unit, angle);
    return;
  }
  if (weapon === WEAPON_DRILL) {
    // Sandbox: the drill starts boring (in a match it has its own command, 'drill').
    startDrill(unit, angle);
    return;
  }
  if (weapon === WEAPON_WAVE) {
    launchSurge(state, unit, cosDeg(angle) < 0 ? -1 : 1);
    return;
  }
  if (weapon === WEAPON_FLAMER) {
    startJet(state, unit, angle);
    return;
  }
  if (weapon === WEAPON_METEOR) {
    dropMeteor(state, unit, target?.[0] ?? (unit.x >> FP_SHIFT));
    return;
  }
  if (weapon === WEAPON_LASER) {
    fireLaser(state, unit, angle);
    return;
  }
  if (weapon === WEAPON_GEYSER) {
    startGeyser(state, target?.[0] ?? (unit.x >> FP_SHIFT), target?.[1] ?? (unit.y >> FP_SHIFT));
    return;
  }
  if (weapon === WEAPON_VOLCANO) {
    startVolcano(state, target?.[0] ?? (unit.x >> FP_SHIFT), target?.[1] ?? (unit.y >> FP_SHIFT), unit.id);
    return;
  }
  if (weapon === WEAPON_REMOTE) {
    if (extra?.remote) moveSquare(state, extra.remote);
    return;
  }
  if (weapon === WEAPON_JETPACK) {
    putJetpackOn(unit);
    return;
  }
  const [cx, cy] = unitCenter(unit);
  const [vx, vy] = launchVelocity(weapon, angle, power);
  state.projectiles.push({
    x: (cx << PFP_SHIFT) + (1 << (PFP_SHIFT - 1)),
    y: (cy << PFP_SHIFT) + (1 << (PFP_SHIFT - 1)),
    vx,
    vy,
    weapon,
    owner: unit.id,
    age: 0,
    link: weapon === WEAPON_DOLL ? extra?.link ?? -1 : -1,
  });
}

/**
 * Starting speed (fixed-point PFP) of a projectile fired along `angle` (integer degrees) with `power` (0-100): the
 * shell's at most MAX_LAUNCH_SPEED, a throw's (Weapon.lob) at most MAX_THROW_SPEED.
 */
export function launchVelocity(weapon: number, angle: number, power: number): [number, number] {
  const speed = Math.trunc(((WEAPONS[weapon]?.lob ? MAX_THROW_SPEED : MAX_LAUNCH_SPEED) * power) / 100);
  return [Math.trunc((speed * cosDeg(angle)) / TRIG_ONE), -Math.trunc((speed * sinDeg(angle)) / TRIG_ONE)];
}

/** Living unit whose body covers cell (x, y), other than `ignore`; null if none. */
export function unitAt(state: GameState, x: number, y: number, ignore: number): Unit | null {
  for (const u of state.units) {
    if (!u.alive || u.id === ignore) continue;
    const ux = u.x >> FP_SHIFT;
    const uy = u.y >> FP_SHIFT;
    if (x >= ux && x < ux + UNIT_W && y >= uy && y < uy + UNIT_H) return u;
  }
  return null;
}

/**
 * The bullet (author's rule: the GDD's is instant, 160 cells): it flies in a straight line, without gravity or wind,
 * BULLET_SPEED cells per tick, until it hits a unit, a mine, a mimic or the ground, or leaves the map
 * (projectile.ts: updateBullet).
 */
function firePistol(state: GameState, unit: Unit, angle: number): void {
  const [cx, cy] = unitCenter(unit);
  state.projectiles.push({
    x: (cx << PFP_SHIFT) + (1 << (PFP_SHIFT - 1)),
    y: (cy << PFP_SHIFT) + (1 << (PFP_SHIFT - 1)),
    vx: Math.trunc((BULLET_SPEED * PFP * cosDeg(angle)) / TRIG_ONE),
    vy: -Math.trunc((BULLET_SPEED * PFP * sinDeg(angle)) / TRIG_ONE),
    weapon: WEAPON_PISTOL,
    owner: unit.id,
    age: 0,
    link: -1,
  });
}

